import React, { useMemo } from "react";
import Button from "./Button";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../Elements/Images/whiteLogo.png";
import { useAuth } from "../../Context/AuthContext";
import { useCategoryFeatures } from "../../hooks/useCategoryFeatures"; // Adjust path as needed
import { profileUrl, publicUrls } from "../../Api/Auth/AppUrls";
import { useContact } from "../../Context/ContactContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

const Footer = () => {
  const { userData, logout, categoryData } = useAuth();
  const { footerPages, contact } = useContact();
  const navigate = useNavigate();
  const firstName = userData?.name ? userData.name.split(" ")[0] : "Guest";

  // Get features from the hook
  const civilFeatures = useCategoryFeatures("Civil");
  const mechanicalFeatures = useCategoryFeatures("Mechanical");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Check visibility for each category
  const showCivil = useMemo(() => {
    return categoryData?.find((cat) => cat.title === "Civil")?.visibility;
  }, [categoryData]);

  const showMechanical = useMemo(() => {
    return categoryData?.find((cat) => cat.title === "Mechanical")?.visibility;
  }, [categoryData]);

  const socialLinks = contact?.socialLinks || {};
  const socialItems = [
    {
      href: socialLinks?.facebook,
      icon: faFacebook,
      label: "Facebook",
    },
    {
      href: socialLinks?.linkedin,
      icon: faLinkedin,
      label: "LinkedIn",
    },
    {
      href: socialLinks?.youtube,
      icon: faYoutube,
      label: "YouTube",
    },
  ].filter((item) => Boolean(item.href));

  return (
    <footer className="bg-primary text-white py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo Column */}
          <div className="flex flex-col items-center gap-8">
            <Link to="/">
              <img
                src={logo}
                alt="Logo"
                className="h-8 max-w-[210px] object-contain transition-transform duration-400 hover:scale-[1.02]"
              />
            </Link>
            {/* Login Column */}
            {userData ? (
              <div className="flex items-center justify-center gap-3">
                <Link
                  to={profileUrl}
                  className="text-lg font-medium capitalize"
                >
                  Hello,
                  <br /> {firstName}
                </Link>
                {userData && (
                  <Button
                    type="outline"
                    onClick={handleLogout}
                    className={"border-white text-white"}
                  >
                    Logout
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col space-y-4 items-center">
                <Link to="login">
                  <Button type="outline" className={"border-white text-white"}>
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button type="outline" className={"border-white text-white"}>
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* About Column */}
          <div className="flex flex-col space-y-4">
            <h3 className="text-lg font-semibold text-gray-400">
              Road to FE Information
            </h3>
            <nav className="flex flex-col space-y-2">
              <Link
                to={publicUrls.Home}
                className="hover:text-purple-400 transition-colors"
              >
                Home
              </Link>

              <Link
                to={publicUrls.About}
                className="hover:text-purple-400 transition-colors"
              >
                About Us
              </Link>
              <Link
                to={publicUrls.NewsArchive}
                className="hover:text-purple-400 transition-colors"
              >
                News Archive
              </Link>
              <Link
                to={publicUrls.BlogsArchive}
                className="hover:text-purple-400 transition-colors"
              >
                Blogs
              </Link>
              <Link
                to={publicUrls.Contact}
                className="hover:text-purple-400 transition-colors"
              >
                Contact Us
              </Link>
            </nav>
          </div>

          {/* Footer Pages Column */}
          {footerPages?.length ? (
            <div className="flex flex-col space-y-4">
              <h3 className="text-lg font-semibold text-gray-400">Resources</h3>
              <nav className="flex flex-col space-y-2">
                {footerPages.map((item) => (
                  <Link
                    key={item.slug}
                    to={publicUrls.staticPage.replace(":slug", item.slug)}
                    className="hover:text-purple-400 transition-colors"
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>
            </div>
          ) : null}

          {/* Civil Course Column - Conditional */}
          {showCivil && (
            <div className="flex flex-col space-y-4">
              <h3 className="text-lg font-semibold text-gray-400">
                OUR FE CIVIL COURSES
              </h3>
              <nav className="flex flex-col space-y-2">
                {civilFeatures.map((item) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="hover:text-purple-400 transition-colors"
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>
            </div>
          )}

          {/* Mechanical Course Column - Conditional */}
          {showMechanical && (
            <div className="flex flex-col space-y-4">
              <h3 className="text-lg font-semibold text-gray-400">
                OUR FE MECHANICAL COURSES
              </h3>
              <nav className="flex flex-col space-y-2">
                {mechanicalFeatures.map((item) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="hover:text-purple-400 transition-colors"
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>
            </div>
          )}
        </div>
        <div className="mb-4 mt-12 border-t border-white/20 pt-6 flex flex-col md:flex-row items-center gap-4 md:gap-6 text-sm text-gray-200">
          <p className="text-center md:text-left flex-1">
            © {new Date().getFullYear()}, Road to FE. All rights reserved.
          </p>
          <nav className="flex items-center gap-4">
            <Link
              to={publicUrls.PrivacyPolicies}
              className="hover:text-purple-300 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="opacity-50">|</span>
            <Link
              to={publicUrls.TermsOfServices}
              className="hover:text-purple-300 transition-colors"
            >
              Terms of Service
            </Link>
          </nav>
          <div className="flex items-center gap-6">
            {socialItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 hover:text-purple-300 transition-colors"
                aria-label={item.label}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  size="lg"
                  className="text-md"
                />
                <span className="text-sm uppercase tracking-wide">
                  {item.label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
